import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import MobileNav from "../components/MobileNav";
import MobileHeader from "../components/Header";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useUserContext } from "../context/userContext";
import { useDrawerContext } from "../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import Button from "../components/Button";
import { useNavigate } from "react-router-dom";
import { useThemeContext } from "../context/themeContext";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase";
import ConnectionDrawer from "../components/ConnectionModal";
import { useLiteDrawer } from "../hooks/useLiteDrawer";

const MIN_SUBSCRIPTION_REFRESH_INTERVAL_MS = 30_000;


const inAppNotif = {
  type: "live-incident",
  link: "",
  status: "inactive",
};

export default function Root() {
  const { user, selectedVenue, selectedZone, refreshSubscription, refreshParticipants } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { showLiteDrawer } = useLiteDrawer();
  const location = useLocation();
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const [activeIncident, setActiveIncident] = useState(null);
  const lastSubscriptionRefreshRef = useRef(0);
  const isRefreshingSubscriptionRef = useRef(false);

  const isGuestBandListing = location.pathname === "/guest-bands" //#F8F8F8
  const isScanPage =
    location.pathname === "/scan" || location.pathname === "/add-band/scan" || location.pathname === "/guest-bands/scan" || location.pathname === "/bookings/scan" || location.pathname === "/bookings/assign-band/scan";
  const isActivity = location.pathname === "/log";
  const isMilestone = location.pathname === "/milestones";
  const isUpgrade = location.pathname === "/upgrade";
  const isIncidents = location.pathname.includes("incident");
  const isVenueParticipants = /^\/venues\/[^/]+\/participants\/?$/.test(
    location.pathname
  );
  const isZoneListing = /^\/venues\/[^/]+\/zones\/[^/]+\/?$/.test(
    location.pathname
  );
  const isAddBand = /^\/add-band\/[^/]+\/?$/.test(location.pathname) || /^\/guest-bands\/[^\/]+\/activate\/[^\/]+\/?$/.test(location.pathname);
  const isBookings = location.pathname === "/bookings";
  const isParticipantBooking = /^\/bookings\/[^/]+\/?$/.test(location.pathname);
  const isStaffBooking = /^\/bookings\/[^/]+\/?$/.test(location.pathname);
  const isParticipantProfile = /^\/bookings\/[^\/]+\/participant\/[^\/]+\/?$/.test(location.pathname);
  const isWeather = /^\/weather\/[^\/]+$/.test(location.pathname);
  const isStaffLog = location.pathname === "/staff-log";
  const isBandProfile = /^\/band\/\d+\/profile$/.test(location.pathname) || /^\/guest-bands\/[a-zA-Z0-9-]+\/profile$/.test(location.pathname);
  const isBandUserProfile = /^\/band\/[^\/]+\/profile$/.test(location.pathname);
  const isParticipant = /^\/participant\/[^\/]+$/.test(location.pathname) || /^\/participant\/[^\/]+\/edit\/?$/.test(location.pathname);
  const isIncident = /^\/incidents\/[^\/]+$/.test(location.pathname);
  const isStaffOnboarding = location.pathname === "/staff-onboarding";
  const isParticipantOnboarding = location.pathname === "/participant-onboarding";
  const isEmergencyScan = location.pathname === "/scan/emergency";
  const isShop = location.pathname.includes("/shop/") && !location.pathname.includes("/shop/checkout");

  useEffect(() => {
    const checkOnboarding = async () => {
      if (theme === "staff") {
        if (user?.staffOnboardingRequired === 1) {
          navigate("/staff-onboarding");
        }
      } else if (theme === "participant") {
        if (user?.userOnboardingRequired === 1) {
          navigate("/participant-onboarding");
        }
      }
    };

    if (theme) {
      checkOnboarding();
    }
  }, [theme]);

  /**
   * The below onMessage function comes from firebase
   * The payload will contain any data that gets sent with the notification
   **/
  onMessage(messaging, (payload) => {
    setActiveIncident(payload);
  });

  useEffect(() => {
    if(activeIncident){
      incidentDrawer();
    }
  }, [activeIncident])

  useEffect(() => {
    if (!theme)
    {
      navigate("/login");
    }
    if (theme === 'staff' && (!selectedVenue || !selectedZone)) {
      navigate("/login");
    }
  }, [theme])

  const incidentDrawer = () => {
    //LogErrors(user, logout, updateTokens, activeIncident);
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h3 className="text-sportsRed">EMERGENCY INCIDENT</h3>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-2 justify-stretch">
          <h3 className="text-[24px]">Emergency Incident Reported!</h3>
          <p className="text-sportsDarkGrey mb-[26px]">
            Go to the incident information.
          </p>
          <div className="flex">
            <Button className="w-full" onClick={handleIncidentNavigation}>View Incident</Button>
          </div>
        </div>
      </div>
    );
  }

  const handleIncidentNavigation = () => {
    const link = activeIncident?.data?.incidentid;
    setActiveIncident(null);
    setOpen(false);
    navigate(`/incidents/${link}`);
  }

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setTimeout(() => {
        window.location.reload(); // ✅ Refresh page when back online
        setOpen(false);
        setDrawerContent(<div></div>);
      }, 1000); // Small delay to avoid flickering issues
    };

    const handleOffline = () => {
      setIsOffline(true);
      setOpen(true); // ✅ Open drawer when offline
      setDrawerContent(<ConnectionDrawer />); // ✅ Set drawer content
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setOpen, setDrawerContent]);

  useEffect(() => {
    if (isOffline) {
      setOpen(true);
      setDrawerContent(<ConnectionDrawer />);
    }
  }, [isOffline, setOpen, setDrawerContent]);

  useEffect(() => {
    if (!user || theme !== "participant") return;

    // Seed so a focus/visibilitychange event firing right after mount doesn't
    // immediately re-run on top of Home's own mount-time subscription fetch.
    lastSubscriptionRefreshRef.current = Date.now();

    const refreshSubscriptionState = () => {
      const now = Date.now();
      if (isRefreshingSubscriptionRef.current) return;
      if (now - lastSubscriptionRefreshRef.current < MIN_SUBSCRIPTION_REFRESH_INTERVAL_MS) return;

      lastSubscriptionRefreshRef.current = now;
      isRefreshingSubscriptionRef.current = true;
      Promise.all([
        refreshSubscription({ onLiteSubscription: showLiteDrawer }),
        refreshParticipants(),
      ]).finally(() => {
        isRefreshingSubscriptionRef.current = false;
      });
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshSubscriptionState();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", refreshSubscriptionState);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", refreshSubscriptionState);
    };
  }, [user, theme, refreshSubscription, refreshParticipants, showLiteDrawer]);


  return user ? (
    <>
      <div className="app-container w-full h-full flex flex-col justify-between">
        <MobileHeader />
        <div
          className={`
          ${
            isScanPage ||
            isActivity ||
            isMilestone ||
            isUpgrade ||
            isIncidents ||
            isZoneListing ||
            isVenueParticipants ||
            isAddBand ||
            isBookings ||
            isStaffBooking ||
            isParticipantBooking ||
            isParticipantProfile ||
            isWeather ||
            isStaffLog ||
            isBandProfile ||
            isParticipant ||
            isIncident ||
            isStaffOnboarding ||
            isParticipantOnboarding ||
            isBandUserProfile ||
            isEmergencyScan ||
            isShop
              ? "!p-0"
              : "p-[24px]"
          } 
          ${
            isActivity ||
            isMilestone ||
            isUpgrade ||
            isIncidents ||
            isZoneListing ||
            isVenueParticipants
              ? "pb-[65px]"
              : "pb-20"
          } 
          ${isGuestBandListing && "!bg-[#F8F8F8]"}
          grow relative bg-white
        `}
        >
          {/* The outlet is a placeholder for page content */}
          <Outlet />
        </div>
        <MobileNav />
      </div>
    </>
  ) : (
    <Navigate to="/login" replace state={{ from: location }} />
  );
}
